/**
 * Save hardened advice payload + export dated actions to calendar (.ics).
 */
import React, { useState } from 'react';
import axios from 'axios';
import { BookmarkIcon, CheckIcon, CalendarDaysIcon } from '@heroicons/react/24/outline';

const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(url);
};

const AdviceSaveActions = ({
  advicePayload,
  visionId = null,
  sourceSurface = 'dashboard',
  sourceRef = null,
  className = '',
  compact = false,
}) => {
  const [savedId, setSavedId] = useState(null);
  const [saveStatus, setSaveStatus] = useState('idle');
  const [calendarStatus, setCalendarStatus] = useState('idle');

  if (!advicePayload?.verdict) return null;

  const handleSave = async () => {
    if (savedId || saveStatus === 'saving') return;
    setSaveStatus('saving');
    try {
      const res = await axios.post('/api/advice/saved', {
        advice_payload: advicePayload,
        vision_id: visionId,
        source_surface: sourceSurface,
        source_ref: sourceRef,
      });
      const id = res.data?.saved?.saved_id;
      setSavedId(id);
      setSaveStatus('saved');
    } catch (err) {
      console.error('Save advice failed:', err);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 2000);
    }
  };

  const handleCalendar = async () => {
    setCalendarStatus('working');
    try {
      let id = savedId;
      if (!id) {
        const res = await axios.post('/api/advice/saved', {
          advice_payload: advicePayload,
          vision_id: visionId,
          source_surface: sourceSurface,
          source_ref: sourceRef,
        });
        id = res.data?.saved?.saved_id;
        setSavedId(id);
        setSaveStatus('saved');
      }
      if (!id) throw new Error('No saved id');

      const icsRes = await axios.get(`/api/advice/saved/${id}/export.ics`, {
        responseType: 'blob',
      });
      downloadBlob(icsRes.data, `aura-action-${id.slice(0, 8)}.ics`);
      setCalendarStatus('done');
      setTimeout(() => setCalendarStatus('idle'), 2000);
    } catch (err) {
      console.error('Calendar export failed:', err);
      setCalendarStatus('error');
      setTimeout(() => setCalendarStatus('idle'), 2000);
    }
  };

  const btnClass = compact
    ? 'inline-flex items-center gap-1 px-2 py-1.5 rounded-md border text-label transition-colors duration-300'
    : 'inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border text-label transition-colors duration-300';

  return (
    <div className={`flex flex-wrap items-center gap-2 ${className}`}>
      <button
        type="button"
        onClick={handleSave}
        disabled={saveStatus === 'saving' || savedId}
        className={`${btnClass} ${
          savedId
            ? 'border-pine bg-pine-tint text-pine'
            : 'border-hairline text-ink-muted hover:bg-pine-tint hover:text-pine hover:border-pine'
        }`}
        title={savedId ? 'Saved' : 'Save advice'}
      >
        {savedId ? <CheckIcon className="w-4 h-4" /> : <BookmarkIcon className="w-4 h-4" />}
        {!compact && <span>{savedId ? 'Saved' : saveStatus === 'saving' ? 'Saving…' : 'Save'}</span>}
      </button>

      <button
        type="button"
        onClick={handleCalendar}
        disabled={calendarStatus === 'working'}
        className={`${btnClass} border-hairline text-ink-muted hover:bg-pine-tint hover:text-pine hover:border-pine`}
        title="Add dated actions to your calendar (.ics)"
      >
        <CalendarDaysIcon className="w-4 h-4" />
        {!compact && (
          <span>
            {calendarStatus === 'working'
              ? 'Preparing…'
              : calendarStatus === 'done'
                ? 'Downloaded'
                : 'Add to calendar'}
          </span>
        )}
      </button>
    </div>
  );
};

export default AdviceSaveActions;
